import { useCallback, useEffect, useState } from 'react';

export interface UserLocation {
    lat: number;
    lng: number;
}

export type GeoStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'error';

const STORAGE_KEY = 'coffee_shops_user_location';
const STORAGE_TTL_MS = 10 * 60 * 1000;

interface CachedLocation extends UserLocation {
    storedAt: number;
}

function readCached(): CachedLocation | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CachedLocation;
        if (Date.now() - parsed.storedAt > STORAGE_TTL_MS) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeCached(loc: UserLocation) {
    try {
        const payload: CachedLocation = { ...loc, storedAt: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // ignore
    }
}

export function useUserLocation(autoRequest = false) {
    const cached = readCached();
    const [location, setLocation] = useState<UserLocation | null>(cached);
    const [status, setStatus] = useState<GeoStatus>(cached ? 'granted' : 'idle');
    const [error, setError] = useState<string | null>(null);

    const request = useCallback(() => {
        if (!('geolocation' in navigator)) {
            setStatus('unsupported');
            setError('Geolocation not supported by this browser');
            return;
        }
        setStatus('requesting');
        setError(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setLocation(next);
                writeCached(next);
                setStatus('granted');
            },
            (err) => {
                if (err.code === err.PERMISSION_DENIED) {
                    setStatus('denied');
                    setError('Location permission denied');
                } else {
                    setStatus('error');
                    setError(err.message || 'Failed to get location');
                }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 }
        );
    }, []);

    useEffect(() => {
        if (autoRequest && !cached && status === 'idle') {
            request();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRequest]);

    return { location, status, error, request };
}

export function haversineKm(a: UserLocation, b: { lat: number; lng: number }): number {
    const R = 6371;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const x =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
}

export function formatDistance(km: number): string {
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
}
